/**
 * AlertBellCounter — la cloche du Centre de notifications.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * ⚠ ELLE A CHANGÉ DE SOURCE — #12+13 P1.1 (décision Hkm : complet dès P1.1).
 *   AVANT : `useActiveAlerts` → `alert_events`, table VIDE D'ORIGINE. Le badge
 *   étant conditionné à `count > 0`, il ne s'est JAMAIS AFFICHÉ de sa vie —
 *   17 semaines à compter du vide sans que rien ne le dise.
 *   APRÈS : `useUnreadCount` → `GET /notifications/unread-count`, les non-lues
 *   DE L'UTILISATEUR COURANT (le compte est fait côté serveur, jamais ici).
 *
 * ⚠ `useActiveAlerts` N'EST PAS SUPPRIMÉ, ET C'EST UNE CORRECTION DU CADRAGE.
 *   Le §7 le donnait pour mourant. Le balayage obligatoire dit l'inverse : il a
 *   QUATRE consommateurs vivants — `AlertingPage` (/admin/alerting, gardée par
 *   #433), `useAlertsLanding` (la landing, INTOUCHABLE), `AlertDrillLevel2`, et
 *   `useUpdateAlertStatus` qui importe sa clé de query. Le supprimer aurait
 *   cassé les deux surfaces que le cadrage protège explicitement.
 *   ⚠ Ce qui meurt ici, c'est LE BRANCHEMENT DE LA CLOCHE — pas le hook.
 *
 * ⚠ LE CLIC MÈNE AU CENTRE, plus à la landing. La landing elle-même n'est pas
 *   touchée : elle reste à sa route, avec son contenu, sans un lien de moins.
 */

import { useT } from '@/lib/i18n';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUnreadCount } from '@/modules/notifications/hooks/useNotifications';
import { ROUTES } from '@/lib/routes';

export function AlertBellCounter() {
  const t = useT();
  const navigate = useNavigate();
  const { data, isLoading } = useUnreadCount();
  const count = data ?? 0;

  return (
    <button
      type="button"
      data-testid="alert-bell-counter"
      /**
       * ⚠ LA CLOCHE OUVRE SUR UNE LISTE DÉPLIÉE, PAS SUR UN ÉCRAN NU (demande Hkm).
       *   Le centre s'ouvre sur ses cadrans seuls (`#480` ③) — c'est juste quand on
       *   y arrive par l'URL, mais cliquer une cloche qui annonce 150 pour tomber
       *   sur une page sans une seule ligne se lit comme un écran cassé.
       * ⚠ LE DÉFAUT NE CHANGE PAS, C'EST LE LIEN QUI PORTE LE FILTRE. `#480` ③
       *   reste intact : la page n'ouvre toujours rien d'elle-même. La cloche fait
       *   un DEEP-LINK, exactement ce pour quoi le filtre a été mis dans l'URL.
       * ⚠ PHÉNOMÈNES parce que c'est la source la plus fournie et la première du
       *   tri. Les quatre autres cadrans restent visibles et cliquables — on ouvre
       *   une porte, on n'en ferme aucune.
       */
      onClick={() => navigate(`${ROUTES.notifications}?source=PHENOMENA`)}
      aria-label={count > 0
          ? t('commun.nNotifNonLues', { n: count })
          : t('nav.centreNotifications')}
      className="relative flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-[var(--surface-hover)] hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-samsung-500/50"
    >
      <Bell
        size={16}
        className={cn(count > 0 ? 'text-accent-danger' : '')}
        aria-hidden="true"
      />
      {/*
        ⚠ AUCUN BADGE À ZÉRO. Un « 0 » collé sur une cloche se lit comme une
          mesure ratée, pas comme le calme. Rien, c'est rien.

        ⚠ LE NOMBRE VRAI, PLUS DE PLAFOND « 99+ » — REQUALIFICATION DU `#442`
          (décision Hkm). Le plafond était un choix d'affichage standard, pris
          quand la cloche n'était qu'un signal de présence. Le contrat PHOTO lui
          a donné un sens qu'il n'avait pas : « la décrue de la cloche EST
          l'amélioration ». Un plafond rend cette décrue INVISIBLE — 150 → 110 se
          lit « 99+ » puis « 99+ ». Le badge ne redeviendrait parlant qu'en
          dessous de 100, autant dire jamais à cinq sources et bientôt six.
        ⚠ LE PROJET PORTAIT DÉJÀ LE PRÉCÉDENT INVERSE : `ClusterMarker.tsx:56`
          — « nombre exact, jamais tronqué "99+" ». Deux compteurs, deux règles
          opposées : celle-ci s'aligne sur la règle qui dit le vrai.
        ⚠ C'EST LA FORME DU BADGE QUI CÈDE, PAS LE NOMBRE : il grandit vers la
          GAUCHE (ancré à droite), donc il ne pousse rien hors du header.
        ⚠ ÉLARGI UNE SECONDE FOIS (demande Hkm) : à `h-4`/`text-[9px]`, trois
          chiffres tenaient mais ne se LISAIENT plus. Un compteur qu'on ne lit
          pas ne compte pas — c'est la même règle que le `#442` requalifié, un
          cran plus loin : afficher le nombre vrai ne sert à rien s'il est
          illisible. La cloche passe donc à `h-[18px]`/`text-[10px]`, avec un
          liseré sombre qui la détache du fond du header.
      */}
      {!isLoading && count > 0 && (
        <span
          data-testid="alert-bell-count"
          className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent-danger px-1.5 text-[10px] font-bold leading-none text-white tabular-nums ring-2 ring-[var(--surface-bg)]"
          aria-hidden="true"
        >
          {count}
        </span>
      )}
    </button>
  );
}
